/**
 * Configuration Service
 * 
 * Provides centralized configuration management for base path and domain settings.
 * Reads from environment variables and provides validated, normalized configuration values.
 */

/**
 * Normalize a path to ensure it has a leading slash and no trailing slash
 * @param {string} path - The path to normalize
 * @returns {string} - Normalized path
 */
function normalizePath(path) {
  if (!path || path.trim() === '') {
    return '';
  }

  let normalized = path.trim();

  // Add leading slash if missing
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }

  // Remove trailing slash(es)
  while (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

/**
 * Validate and sanitize a domain string
 * @param {string} domain - The domain to validate
 * @returns {string} - Sanitized domain or empty string if invalid
 */
function validateAndSanitizeDomain(domain) {
  if (!domain || domain.trim() === '') {
    return '';
  }

  const trimmed = domain.trim();
  
  // Basic domain validation: alphanumeric, dots, hyphens, and colons (for ports)
  // Allow localhost and IP addresses with ports
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-_.:])*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/;
  
  if (!domainRegex.test(trimmed)) {
    console.warn(`[Config] Invalid domain format: "${domain}". Sanitizing...`);
    // Remove invalid characters
    const sanitized = trimmed.replace(/[^a-zA-Z0-9-_.:]/g, '');
    return sanitized;
  }

  return trimmed;
}

/**
 * Load and validate configuration from environment variables
 * @returns {Object} - Configuration object
 */
function loadConfig() {
  // Read environment variables
  // Prefer CRA-exposed variables first, then fallback to plain ones (useful for tests/node)
  const envDomain = process.env.REACT_APP_DOMAIN || process.env.DOMAIN;
  let envBasePath = process.env.REACT_APP_BASE_PATH || process.env.BASE_PATH;

  // Create React App does not expose arbitrary env vars to the client bundle,
  // but it does inject PUBLIC_URL. If BASE_PATH isn't available at runtime,
  // derive the path from PUBLIC_URL as a fallback so routing works under a subdir.
  if (!envBasePath) {
    const publicUrl = process.env.PUBLIC_URL;
    if (typeof publicUrl === 'string' && publicUrl.trim() !== '') {
      try {
        const raw = publicUrl.trim();
        let derivedPath = '';
        if (raw.startsWith('http://') || raw.startsWith('https://')) {
          // Absolute URL: extract pathname
          const u = new URL(raw);
          derivedPath = u.pathname || '';
        } else {
          // Relative like "/app" or just "/" or "app"
          derivedPath = raw;
        }
        envBasePath = derivedPath;
      } catch (_) {
        // Ignore malformed PUBLIC_URL
      }
    }
  }

  // Get protocol from current page
  const protocol = typeof window !== 'undefined' 
    ? window.location.protocol 
    : 'https:';

  // Get domain with fallback to current hostname
  let domain = envDomain;
  if (!domain && typeof window !== 'undefined') {
    domain = window.location.hostname;
    if (window.location.port && window.location.port !== '80' && window.location.port !== '443') {
      domain = `${domain}:${window.location.port}`;
    }
  }

  // Validate and sanitize domain
  domain = validateAndSanitizeDomain(domain || '');
  if (!domain && typeof window !== 'undefined') {
    // Final fallback
    domain = window.location.host;
  }

  // Normalize base path
  let basePath = normalizePath(envBasePath || '');
  // Treat "/" as root (empty) for React Router basename to avoid oddities
  if (basePath === '/') basePath = '';

  // Construct root path
  const rootPath = domain 
    ? `${protocol}//${domain}${basePath}`
    : basePath;

  return {
    domain,
    basePath,
    protocol,
    rootPath
  };
}

// Initialize configuration
const config = loadConfig();

// Log configuration for debugging
console.log('[Config] Application configuration initialized:', {
  domain: config.domain,
  basePath: config.basePath,
  protocol: config.protocol,
  rootPath: config.rootPath
});

/**
 * Get the base path for React Router
 * @returns {string} - Base path (e.g., "/cascade" or "")
 */
export function getBasePath() {
  return config.basePath;
}

/**
 * Get the full root path including protocol and domain
 * @returns {string} - Root path (e.g., "https://devland.example.com/cascade")
 */
export function getRootPath() {
  return config.rootPath;
}

/**
 * Get the configured domain
 * @returns {string} - Domain (e.g., "devland.example.com")
 */
export function getDomain() {
  return config.domain;
}

/**
 * Get the protocol (http or https)
 * @returns {string} - Protocol (e.g., "http:" or "https:")
 */
export function getProtocol() {
  return config.protocol;
}

// Export for testing purposes
export const __testing__ = {
  normalizePath,
  validateAndSanitizeDomain,
  loadConfig
};
